// client/src/features/expenses/search.ts — the URL state of SCR-EXP-001 and SCR-EXP-004 ·
// EXP-005 (04 §9.2.3).
//
// §9.2.3 is NORMATIVE for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered ledger is shareable, survives a refresh, and
// browser Back is a filter-undo. `features/tenants/search.ts` is the shape this copies.
//
// The ledger's filter names are the endpoint's own — `propertyId`, `categoryId`, `status`,
// `from`, `to`, `isCapex`, `isRecurring`, `q` — so the URL and the request say the same words. The one that is
// NOT a query parameter is the property: `GET /api/v1/properties/{propertyId}/expenses` is a
// second route with the same envelope and the same filters, and picking a property switches the
// screen onto it (see `queries.ts`).

import { z } from 'zod';

import { urlBoolean, zUrlBoolean } from '@/lib/url-state';

import { EXPENSE_STATUSES, INVOICE_PROCESSING_STATUSES } from './enums';

/**
 * Newest cost first. `expenseDate` is one of the six fields `ExpensesEndpoints.Sortable`
 * declares, and it is the store's own default — spelled here anyway so a shared URL carries the
 * order it was read in.
 */
export const DEFAULT_EXPENSE_SORT = 'expenseDate:desc';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const expenseSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().default(DEFAULT_EXPENSE_SORT),
  q: z.string().trim().optional(),
  propertyId: z.string().trim().optional(),
  categoryId: z.string().trim().optional(),
  status: z.enum(EXPENSE_STATUSES).optional(),
  from: z.string().trim().optional(),
  to: z.string().trim().optional(),
  /**
   * The CapEx split of 03 §16.3 E2 — „investiție" against „cost operațional".
   *
   * Two things kept this from becoming a plain boolean. `z.coerce.boolean()` maps the string
   * „false" to `true`, which would make the second of three options unreachable; and a
   * `.transform()` to a real boolean would make the schema's input and output types differ,
   * which TanStack Router's `navigate({ search })` reducer refuses — it writes the INPUT shape
   * and reads back the output. `toLedgerQuery` does the one conversion, where the request is
   * built (D-713).
   *
   * It admits BOTH spellings — see `lib/url-state.ts`. Clicking the control writes the string
   * „true"; a URL somebody shared or typed carries `?isCapex=true`, which the router's
   * `JSON.parse` turns into the boolean, and a schema that took only the string replaced the
   * whole screen with a Zod dump on the shared link (D-1197).
   */
  isCapex: zUrlBoolean.optional(),
  /**
   * The „Origine" split (D-2922): the rows a recurring rule generated, against the costs typed by
   * hand. The same tri-state and the same two spellings as `isCapex`, for the same reasons.
   */
  isRecurring: zUrlBoolean.optional(),
});

export type ExpenseSearch = z.infer<typeof expenseSearchSchema>;

/** The URL's tri-state as the endpoint's `isCapex` query parameter takes it. */
export function isCapexFilter(search: ExpenseSearch): boolean | undefined {
  return urlBoolean(search.isCapex);
}

/** The URL's tri-state as the endpoint's `isRecurring` query parameter takes it (D-2922). */
export function isRecurringFilter(search: ExpenseSearch): boolean | undefined {
  return urlBoolean(search.isRecurring);
}

/** Which filters are engaged — the empty state needs to know why it is empty. */
export function hasActiveExpenseFilters(search: ExpenseSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.propertyId !== undefined ||
    search.categoryId !== undefined ||
    search.status !== undefined ||
    search.from !== undefined ||
    search.to !== undefined ||
    search.isCapex !== undefined ||
    search.isRecurring !== undefined
  );
}

/** Clearing filters returns to page 1: page 3 of a set that no longer exists renders nothing. */
export const CLEARED_EXPENSE_SEARCH: ExpenseSearch = {
  page: 1,
  pageSize: 25,
  sort: DEFAULT_EXPENSE_SORT,
};

// ── SCR-EXP-004: „Furnizori & facturi", one route and two lists ───────────────────────────

/** `SupplierStore`'s own default order — a registry is read by name. */
export const DEFAULT_SUPPLIER_SORT = 'name:asc';

/** `InvoiceStore`'s default is `createdAtUtc:desc`; the register is read newest first. */
export const DEFAULT_INVOICE_SORT = 'issueDate:desc';

/**
 * Both lists live on one route (04 §8.7 gives SCR-EXP-004 a single URL), so their parameters
 * share one schema and are prefixed by owner. Without the prefix a `q` would filter both lists
 * at once and neither of them the way the user meant.
 */
export const supplierSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().optional(),
  invoicePage: z.coerce.number().int().min(1).default(1),
  invoiceQ: z.string().trim().optional(),
  invoiceSupplierId: z.string().trim().optional(),
  invoiceStatus: z.enum(INVOICE_PROCESSING_STATUSES).optional(),
});

export type SupplierSearch = z.infer<typeof supplierSearchSchema>;

export function hasActiveSupplierFilters(search: SupplierSearch): boolean {
  return search.q !== undefined && search.q !== '';
}

export function hasActiveInvoiceFilters(search: SupplierSearch): boolean {
  return (
    (search.invoiceQ !== undefined && search.invoiceQ !== '') ||
    search.invoiceSupplierId !== undefined ||
    search.invoiceStatus !== undefined
  );
}
